// Libraries
import { Column, Table } from '@tanstack/react-table';
// Components
import { FilterGroupsType, FilterOption } from '@organisms/index';
// Types
import { ColumnsGroups } from './TableFilters.types';

/**
 * Get distinct options from a specific column in a table.
 *
 * @function
 * @template TableBody - The type of the table body.
 * @param {Table<TableBody>} table - The table object.
 * @param {string} columnId - The ID of the column.
 * @returns {Array<number | string>} An array of distinct options from the specified column.
 */
const getOptions = <TableBody>(table: Table<TableBody>, columnId: string) => {
  const rowOptions = table.getCoreRowModel().rows.map((row) => row.getValue(columnId));
  return [...new Set(rowOptions)].filter((option) => typeof option === 'number' || typeof option === 'string') as (
    | number
    | string
  )[];
};

/**
 * Get filter groups based on the provided filter options.
 *
 * @function
 * @template TableBody - The type of the table body.
 * @param {Table<TableBody>} table - The table object.
 * @param {FilterOption[]} filterOptions - The filter options.
 * @returns {ColumnsGroups<TableBody>} The filter groups containing visible and hidden columns.
 */
export const getFiltersGroups = <TableBody>(table: Table<TableBody>, filterOptions: FilterOption[]) =>
  filterOptions.reduce<ColumnsGroups<TableBody>>(
    (acc, { hidden, columnId, groupId }) => {
      const columnProps = table.getColumn(columnId) as Column<TableBody, unknown>;
      const options = getOptions(table, columnId);
      const displayLocation = hidden ? 'hidden' : 'visible';

      if (groupId !== FilterGroupsType.SINGLE) {
        if (!acc[displayLocation][groupId]) {
          acc[displayLocation][groupId] = [];
        }
        acc[displayLocation][groupId].push({ ...columnProps, options });
        return acc;
      }
      acc[displayLocation][columnId] = [{ ...columnProps, options }];
      return acc;
    },
    {
      hidden: {},
      visible: {},
    }
  );
